import { useEffect, useMemo, useState } from "react";

import type { AxiosInstance } from "axios";
import { useHistory, useLocation } from "react-router-dom";

import { toastError } from "@alpacahq/ui/old";

import { useGetOwnerDetails, useIsCryptoSupported } from "~/v2/api/hooks";
import {
  useCryptoWalletAPI,
  useGetCryptoAssets,
  useGetCryptoWhitelists,
} from "~/v2/api/hooks/useGetCrypto";
import type { OwnerDetails } from "~/v2/api/rest/account";
import type { CryptoWhitelistedAddress } from "~/v2/api/rest/cryptoWallet";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { CryptoSelectOption } from "../presentation/CryptoSelect";
import { getCryptoAssetDisplayName, groupAssetsByChain } from "../util";
import { STEP_CONFIG } from "./const";
import type { TravelRuleFormValues } from "./TravelRuleForm/schema";
import { mapTravelRuleToFormDefaults } from "./TravelRuleForm/travelRuleDefaults";
import type {
  AssetAddressData,
  AssetAddressDisplay,
  WhitelistAddressMode,
  WhitelistAddressStep,
} from "./types";

export interface WhitelistAddressControllerReturn {
  step: WhitelistAddressStep;
  stepConfig: (typeof STEP_CONFIG)[WhitelistAddressStep];
  isEditMode: boolean;
  isLoading: boolean;
  chainOptions: CryptoSelectOption[];
  assetOptionsByChain: Record<string, CryptoSelectOption[]>;
  chainNameByCode: Record<string, string>;
  accountId: string;
  accountDetails?: OwnerDetails;
  cryptoWalletAxiosInstance: AxiosInstance;
  mode: WhitelistAddressMode;
  assetAddressData: AssetAddressData;
  assetAddressDisplay: AssetAddressDisplay;
  travelRuleInitialValues: Partial<TravelRuleFormValues>;
  onAssetAddressNext: (data: AssetAddressData) => void;
  onCancel: () => void;
  onSuccess: () => void;
  onBackToAssetAddress: () => void;
}

export const useWhitelistAddressController =
  (): WhitelistAddressControllerReturn => {
    const history = useHistory();
    const location = useLocation();

    const whitelistId = useMemo(() => {
      const params = new URLSearchParams(location.search);
      return params.get("whitelistId") ?? null;
    }, [location.search]);

    const { currentAccount } = useAccountContext({ liveOnly: true });
    const accountId = currentAccount?.id ?? "";

    const { details } = useGetOwnerDetails();

    const { isCryptoSupported, isLoading: isCryptoSupportedLoading } =
      useIsCryptoSupported();

    const isCryptoActive =
      currentAccount?.crypto_status === "ACTIVE" &&
      !currentAccount?.admin_configurations?.disable_crypto;
    const cryptoWalletAxiosInstance = useCryptoWalletAPI();

    const isAccountLoading = !currentAccount || !details;

    const isFetchingEnabled = useMemo(
      () =>
        !isAccountLoading &&
        isCryptoActive &&
        isCryptoSupported &&
        !!details?.crypto_agreement_signed_at,
      [
        isAccountLoading,
        isCryptoActive,
        isCryptoSupported,
        details?.crypto_agreement_signed_at,
      ],
    );

    const assets = useGetCryptoAssets({
      accountId,
      options: { enabled: isFetchingEnabled },
    });

    const whitelists = useGetCryptoWhitelists({
      accountId,
      options: { enabled: isFetchingEnabled && !!whitelistId },
    });

    const existingWhitelist = useMemo<CryptoWhitelistedAddress | null>(() => {
      if (!whitelistId || !whitelists.data) {
        return null;
      }
      return whitelists.data.find((w) => w.id === whitelistId) ?? null;
    }, [whitelistId, whitelists.data]);

    const isInvalidEditId =
      !!whitelistId &&
      !whitelists.isLoading &&
      whitelists.isSuccess &&
      !existingWhitelist;

    const mode = useMemo<WhitelistAddressMode>(() => {
      if (whitelistId && existingWhitelist) {
        return { kind: "edit", whitelistId, existing: existingWhitelist };
      }
      return { kind: "create" };
    }, [whitelistId, existingWhitelist]);

    const { chainOptions, assetOptionsByChain, chainNameByCode } = useMemo(
      () => groupAssetsByChain(assets?.data ?? []),
      [assets.data],
    );

    // In create mode these are the user-controlled source of truth; in edit mode
    // the public `step`/`assetAddressData` are derived from `mode` so existing
    // values render on the same tick `mode` resolves (no flash, no syncing effect).
    const [createStep, setCreateStep] =
      useState<WhitelistAddressStep>("assetAddress");
    const [createAssetAddressData, setCreateAssetAddressData] =
      useState<AssetAddressData>({
        chain: "",
        asset: "",
        address: "",
      });

    const step: WhitelistAddressStep =
      mode.kind === "edit" ? "travelRule" : createStep;
    const assetAddressData: AssetAddressData =
      mode.kind === "edit"
        ? {
            chain: mode.existing.chain,
            asset: mode.existing.asset,
            address: mode.existing.address,
          }
        : createAssetAddressData;

    // Friendly labels for the TR form's read-only Basic Info panel; falls back
    // to raw codes when assets haven't loaded.
    const assetAddressDisplay = useMemo(() => {
      const chainOption = chainOptions.find(
        (o) => o.value === assetAddressData.chain,
      );
      const assetOption = assetOptionsByChain[assetAddressData.chain]?.find(
        (o) => o.value === assetAddressData.asset,
      );
      return {
        chainCode: assetAddressData.chain,
        chainLabel: chainOption?.label ?? assetAddressData.chain,
        assetCode: getCryptoAssetDisplayName(
          assetAddressData.chain,
          assetAddressData.asset,
        ),
        assetLabel: assetOption?.label ?? assetAddressData.asset,
      };
    }, [
      assetAddressData.chain,
      assetAddressData.asset,
      chainOptions,
      assetOptionsByChain,
    ]);

    const onAssetAddressNext = (data: AssetAddressData) => {
      setCreateAssetAddressData(data);
      setCreateStep("travelRule");
    };

    const onCancel = () => history.push(Path.ROUTE_CRYPTO_TRANSFERS);
    const onSuccess = () => history.push(Path.ROUTE_CRYPTO_TRANSFERS);

    // useTravelRuleForm derives `beneficiaryType` from these defaults
    // (entity_name => "business", any name => "individual").
    const travelRuleInitialValues = useMemo<
      Partial<TravelRuleFormValues>
    >(() => {
      if (mode.kind !== "edit" || !mode.existing.travel_rule_info) {
        return {};
      }
      return mapTravelRuleToFormDefaults(mode.existing.travel_rule_info);
    }, [mode]);

    // Edit mode has no back path — the button is Cancel.
    const onBackToAssetAddress = () => {
      if (mode.kind === "edit") {
        onCancel();
        return;
      }
      setCreateStep("assetAddress");
    };

    useEffect(() => {
      if (!currentAccount || isCryptoSupportedLoading) {
        return;
      }
      if (!isCryptoSupported || !isCryptoActive) {
        history.replace(Path.ROUTE_DASHBOARD);
        return;
      }
    }, [
      currentAccount,
      isCryptoSupportedLoading,
      isCryptoSupported,
      isCryptoActive,
      history,
    ]);

    useEffect(() => {
      if (isInvalidEditId) {
        toastError("Whitelist address not found");
        history.replace(Path.ROUTE_CRYPTO_TRANSFERS);
      }
    }, [isInvalidEditId, history]);

    const isLoading =
      !currentAccount ||
      !details ||
      isCryptoSupportedLoading ||
      assets.isLoading ||
      (!!whitelistId && whitelists.isLoading) ||
      isInvalidEditId;

    const isEditMode = mode.kind === "edit";

    return {
      step,
      stepConfig: STEP_CONFIG[step],
      isEditMode,
      isLoading,
      chainOptions,
      assetOptionsByChain,
      chainNameByCode,
      accountId,
      accountDetails: details,
      cryptoWalletAxiosInstance,
      mode,
      assetAddressData,
      assetAddressDisplay,
      travelRuleInitialValues,
      onAssetAddressNext,
      onCancel,
      onSuccess,
      onBackToAssetAddress,
    };
  };
